import React, { useState, useEffect, forwardRef, useContext } from 'react';
import Context from '../../../utils/context';
import styles from './index.module.css';

const ImportantSafetyInformationStickyFooterV2 = forwardRef(
  (
    {
      component,
      initialHeight = 150,
      initialHeightMobile = 150,
      expandedHeight = 600,
      autoExpandOnScroll = false,
      startCollapsed = false,
    },
    ref
  ) => {
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [isSticky, setIsSticky] = useState(true);
    const [refOffset, setRefOffset] = useState(0);
    const [staticPoint, setStaticPoint] = useState(0);
    const [initialPageHeight, setInitialPageHeight] = useState(0);
    const context = useContext(Context);
    const isMobile = context.viewport.getIsMobile();
    const activeInitialHeight = isMobile && initialHeightMobile != null ? initialHeightMobile : initialHeight;
    const [footerHeight, setFooterHeight] = useState(activeInitialHeight);
    const rect = ref?.current?.getBoundingClientRect();

    useEffect(() => {
      const footerEl = document.querySelector('#site-footer');
      setStaticPoint(footerEl ? document.documentElement.scrollHeight - footerEl.offsetHeight : Infinity);
      setInitialPageHeight(window.innerHeight);
    }, []);

    useEffect(() => {
      const updateRefOffset = () => {
        if (!ref.current) return;
        if (!rect) return;
        if (refOffset > 0) return;

        const scrollTop = window.scrollY || document.documentElement.scrollTop;

        setRefOffset(rect.top + scrollTop);
      };

      updateRefOffset();
      window.addEventListener('resize', updateRefOffset);
      return () => window.removeEventListener('resize', updateRefOffset);
    }, [ref.current]);

    useEffect(() => {
      const shrunkHeight = Math.round(activeInitialHeight * 0.5);

      const handleScroll = () => {
        if (!ref.current) return;
        if (!rect) return;

        const shrinkPoint = activeInitialHeight;
        const buffer = 20;

        if (autoExpandOnScroll) {
          if (window.scrollY > shrinkPoint) {
            setIsExpanded(true);
          } else if (window.scrollY <= 0) {
            setIsExpanded(false);
          }
        } else if (!isExpanded && !startCollapsed) {
          setFooterHeight(window.scrollY > shrinkPoint ? shrunkHeight : activeInitialHeight);
        }

        const shouldBeSticky = window.scrollY + initialPageHeight + buffer <= staticPoint;

        setIsSticky(shouldBeSticky);
      };

      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }, [refOffset, isExpanded, activeInitialHeight, autoExpandOnScroll, startCollapsed]);

    useEffect(() => {
      const maxHeight = window.innerHeight;
      setFooterHeight(isExpanded ? Math.min(expandedHeight, maxHeight) : activeInitialHeight);
    }, [isExpanded, expandedHeight, activeInitialHeight]);

    const handleRotate = () => {
      setIsExpanded((prev) => !prev);
    };

    return (
      <>
        {/* Reserves in-flow scroll room matching the fixed footer so it can
            never permanently cover the content above it. */}
        {isSticky && <div aria-hidden='true' style={{ height: `${activeInitialHeight}px` }} />}
        <div
          ref={ref}
          className={isSticky ? styles.sticky_footer_container : styles.sticky_footer_container_normal}
          style={isSticky ? { height: `${footerHeight}px` } : {}}
        >
          <div className={`${styles.svg_container} ${isExpanded ? styles.rotated : ''}`} onClick={handleRotate}>
            <svg viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <g id='SVGRepo_iconCarrier'>
                <path
                  d='M18.2929 15.2893C18.6834 14.8988 18.6834 14.2656 18.2929 13.8751L13.4007 8.98766C12.6195 8.20726 11.3537 8.20757 10.5729 8.98835L5.68257 13.8787C5.29205 14.2692 5.29205 14.9024 5.68257 15.2929C6.0731 15.6835 6.70626 15.6835 7.09679 15.2929L11.2824 11.1073C11.673 10.7168 12.3061 10.7168 12.6966 11.1073L16.8787 15.2893C17.2692 15.6798 17.9024 15.6798 18.2929 15.2893Z'
                  fill='#0F0F0F'
                ></path>
              </g>
            </svg>
          </div>
          <footer className={styles.isi_footer} style={isSticky ? { height: `${footerHeight}px` } : {}}>
            {component}
          </footer>
        </div>
      </>
    );
  }
);

export default ImportantSafetyInformationStickyFooterV2;
